import React, { useEffect, useState } from "react";
import {
  createCampaignApi,
  createCampaignNoSessionApi,
  createProductAnalysisApi,
  createProductAnalysisOpenApi,
} from "@/api/createCampaignApi";
import { useTranslation } from "react-i18next";
import { websiteHostValidator, websiteValidator } from "@/biz/validator";
import { CampaignStatusEnum } from "@/components/campaignCard/config";
import { StopIcon } from "@/components/icons";
import { useQuery } from "@/hooks/useQuery";
import removeExtraProtocol from "@/utils/removeExtraProtocol";
import { useClerk, useUser } from "@clerk/clerk-react";
import { Button, Form, Input, message } from "antd";
import { useNavigate } from "react-router-dom";
import { CreateCampaignStageEnum } from "../../config";
import { getUseCaseMatchApi } from "@/api/organization";

export default function WebsiteForm({
  details,
  stage,
  setProductInfoLoading,
  setWebsiteLoading,
  setShowStop,
  websiteLoading,
  isShowStop,
  setWebsite,
  website,
  productForm,
  existWebsite,
  setExistWebsite,
  setProductInfo,
  setCaseInfo,
}: {
  details: any;
  stage: CreateCampaignStageEnum;
  setProductInfoLoading: (loading: boolean) => void;
  setWebsiteLoading: (loading: boolean) => void;
  setShowStop: (show: boolean) => void;
  websiteLoading: boolean;
  isShowStop: boolean;
  setWebsite: (website: string) => void;
  website: string;
  productForm: any;
  existWebsite: string;
  setExistWebsite: (website: string) => void;
  setProductInfo: (productInfo: any) => void;
  setCaseInfo: (caseInfo: any) => void;
}) {
  const { t } = useTranslation();
  const { user } = useClerk();
  const { isSignedIn } = useUser();
  const campaignId = useQuery("campaignId");
  const navigate = useNavigate();
  // 如果是发布之后的编辑
  const isActive = details?.status === CampaignStatusEnum.ACTIVE;

  const getUseCase = async (values: { website: string }) => {
    try {
      const res = await getUseCaseMatchApi({
        domain: values?.website,
        count: 1,
      });
      setCaseInfo(res?.[0] || {});
    } catch (error) {}
  };

  const handleCreate = async (values: {
    website: string;
    campaignId?: string;
  }) => {
    try {
      setWebsiteLoading(true);
      setShowStop(true);
      setProductInfo(null);
      const id = values?.campaignId || campaignId;
      if (values?.website && !values.website?.startsWith("https://")) {
        values.website = "https://" + values.website;
      }
      if (!isSignedIn) {
        const tempOrgId = localStorage.getItem("tempOrgId")
          ? localStorage.getItem("tempOrgId")
          : Date.now().toString(36).substring(2, 12);
        localStorage.setItem("tempOrgId", String(tempOrgId));
        const { data } = await createProductAnalysisOpenApi(
          id,
          values,
          String(tempOrgId)
        );
        setExistWebsite(values.website?.replace("https://", ""));
      } else {
        await createProductAnalysisApi(id, values);
        setExistWebsite(values.website?.replace("https://", ""));
      }
      setProductInfoLoading(true);
      getUseCase(values);
    } catch (error: any) {
      // API 中 throw new Error(error?.message)，所以 error 是 Error 实例，使用 error?.message 更稳妥
      message.error(error?.message || t("campaignCreate.errors.networkError"));
    }
  };
  const handleWebsiteFinish = async (values: { website: string }) => {
    handleCreate(values);
  };

  const handleCreateCampaign = async () => {
    if (!isSignedIn) {
      const tempOrgId = localStorage.getItem("tempOrgId")
        ? localStorage.getItem("tempOrgId")
        : Date.now().toString(36).substring(2, 12);
      localStorage.setItem("tempOrgId", String(tempOrgId));
      const { campaignId } = await createCampaignNoSessionApi(
        tempOrgId as string
      );
      navigate(`/campaign/website/create/${stage}?campaignId=${campaignId}`, {
        replace: true,
      });
      setTimeout(() => {
        handleCreate({ website: url, campaignId });
      }, 1000);
    } else {
      const { campaignId } = await createCampaignApi();
      navigate(`/campaign/website/create/${stage}?campaignId=${campaignId}`, {
        replace: true,
      });
      setTimeout(() => {
        handleCreate({ website: url, campaignId });
      }, 1000);
    }
  };
  // 官网点击过来的流程
  const url = useQuery("url");
  useEffect(() => {
    if (websiteValidator(url)) {
      productForm.setFieldValue("website", url);
      setWebsite(url?.replace("https://", ""));
      setTimeout(() => {
        handleCreateCampaign();
      });
    }
  }, [url]);

  useEffect(() => {
    if (website) {
      productForm.setFieldValue("website", website);
    }
  }, [website]);

  return (
    <div className="p-4 bg-white rounded-[20px] border border-[#EEEEEE] inline-flex flex-col justify-start items-start gap-4 w-full">
      <div className="self-stretch justify-start text-[#1c2024] text-base font-normal  leading-7">
        {user?.firstName
          ? t("campaignCreate.websiteForm.welcomeMessage", {
              firstName: user.firstName,
            })
          : t("campaignCreate.websiteForm.welcomeMessageNoName")}
      </div>
      <div className={`flex gap-4 w-full`}>
        <Form
          className="flex-1"
          name="select-product"
          form={productForm}
          onFinish={handleWebsiteFinish}
          initialValues={{ website: "" }}
        >
          <Form.Item
            name="website"
            className="mb-0"
            rules={[
              {
                validator: (_, value) => {
                  if (!value) {
                    return Promise.reject(
                      new Error(
                        t("campaignCreate.websiteForm.pleaseEnterWebsite")
                      )
                    );
                  }
                  return websiteHostValidator(value)
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          t(
                            "campaignCreate.websiteForm.pleaseEnterValidWebsite"
                          )
                        )
                      );
                },
              },
            ]}
          >
            <Input
              className="url-input-style"
              placeholder={t("campaignCreate.websiteForm.websitePlaceholder")}
              size="large"
              disabled={websiteLoading || isActive}
              onChange={(e) => {
                if (e?.target?.value?.startsWith("https://")) {
                  setWebsite(e?.target?.value?.replace("https://", ""));
                } else {
                  setWebsite(e?.target?.value);
                }
              }}
              addonBefore="https://"
              defaultValue="mysite"
              onBlur={(e) => {
                let input = e.target.value;
                const value = removeExtraProtocol(input);

                setWebsite(value?.replace("https://", ""));
                productForm.setFieldValue("website", value);
              }}
            />
          </Form.Item>
        </Form>
        {isShowStop ? (
          <Button
            size="large"
            onClick={() => {
              setWebsiteLoading(false);
              setShowStop(false);
            }}
            className="w-[94px] black-button"
          >
            <StopIcon />
          </Button>
        ) : (
          <Button
            size="large"
            loading={websiteLoading}
            disabled={websiteLoading || isActive || website === existWebsite}
            onClick={() => productForm.submit()}
            className="w-[94px] black-button"
          >
            {t("campaignCreate.websiteForm.generate")}
          </Button>
        )}
      </div>
    </div>
  );
}
